import type { Workspace } from "common/types";

type OutreachLicenseCountWorkspace = Pick<
  Workspace,
  "outreach_license_count" | "license_count" | "has_active_rental_subscription"
>;

export function hasActiveOutreachSubscription(
  workspace: Pick<Workspace, "role" | "subscription_status">,
): boolean {
  return (
    workspace.role === "owner" &&
    workspace.subscription_status &&
    workspace.subscription_status !== "cancelled"
  );
}

export function getOutreachLicenseCount(
  workspace: OutreachLicenseCountWorkspace | null | undefined,
  fallback?: number,
): number | undefined {
  if (workspace?.outreach_license_count != null) {
    return workspace.outreach_license_count;
  }
  // Legacy: license_count on list was total when rental exists — avoid wrong fallback
  if (
    !workspace?.has_active_rental_subscription &&
    workspace?.license_count != null
  ) {
    return workspace.license_count;
  }
  return fallback;
}

type WorkspaceDetailsForOutreach = Pick<
  Workspace,
  "outreach_license_count" | "subscription_status"
> & {
  subscription?: { status: Workspace["subscription_status"] } | null;
};

export function mergeWorkspacesWithDetails(
  workspaces: Workspace[] | undefined,
  detailsById: Map<string, WorkspaceDetailsForOutreach>,
): Workspace[] | undefined {
  if (!workspaces) return undefined;

  return workspaces.map((workspace) => {
    const details = detailsById.get(workspace.id);
    if (!details) return workspace;

    return {
      ...workspace,
      subscription_status:
        workspace.subscription_status ??
        details.subscription_status ??
        details.subscription?.status ??
        workspace.subscription_status,
      outreach_license_count:
        details.outreach_license_count ?? workspace.outreach_license_count,
    };
  });
}

export function getActiveOutreachWorkspaceSeatCounts(
  workspaces: Workspace[] | undefined,
): number[] {
  if (!workspaces) return [];

  return workspaces
    .filter(hasActiveOutreachSubscription)
    .map((workspace) => getOutreachLicenseCount(workspace) ?? 0);
}

export function getTotalActiveOutreachSeats(
  workspaces: Workspace[] | undefined,
): number {
  return getActiveOutreachWorkspaceSeatCounts(workspaces).reduce(
    (sum, count) => sum + count,
    0,
  );
}
